import { LayoutType, SpeakerDirectionType } from '@goldcast/player-config/src/types/asset-types';
import type { PipPosition } from '@goldcast/utils/src/media/speakerLayoutConfig';

// Order the layout cards appear in the new grid. Values map 1:1 to the existing
// LayoutEnum; "Shared Fill" / "Film Strip" from the Figma are deferred to a
// later iteration (CR-10349), so GRID and FOCUS are kept as-is.
export const NEW_LAYOUT_CARD_ORDER: LayoutType[] = ['DEFAULT', 'GRID', 'FOCUS', 'PIP', 'SPEAKER', 'AUDIOGRAM'];

// UI-only placeholder controls. These have no persisted magicLayout field yet,
// so they are visual-only until a backend schema lands (CR-10349 follow-up).

// Aspect-ratio segmented selector. Superset of the real SizeType — `STANDARD`
// (4:5) has no persisted equivalent yet, so the whole control stays UI-only
// until the backend schema lands (CR-10349 follow-up).
export type AspectRatio = 'LANDSCAPE' | 'SQUARE' | 'PORTRAIT';

// `ratio` is width / height; the selector draws each option's preview box from it.
export const ASPECT_RATIO_SELECTOR_OPTIONS: { value: AspectRatio; label: string; ratio: number }[] = [
  { value: 'LANDSCAPE', label: '16:9', ratio: 16 / 9 },
  { value: 'SQUARE', label: '1:1', ratio: 1 },
  // { value: 'STANDARD', label: '4:5', ratio: 4 / 5 },
  { value: 'PORTRAIT', label: '9:16', ratio: 9 / 16 }
];

export type SpeakerPosition = SpeakerDirectionType;
export type SpeakerTileShape = 'SQUARE' | 'ROUND';
export type PipCorner = 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT';

// Maps the 2x2 corner picker to the persisted PipPosition. PipPosition also has
// LEFT_MIDDLE/RIGHT_MIDDLE/TOP_MIDDLE/BOTTOM_MIDDLE values that the 4-corner UI
// doesn't expose yet, so those are only handled on the read side (below).
export const PIP_CORNER_TO_POSITION: Record<PipCorner, PipPosition> = {
  TOP_LEFT: 'LEFT_TOP',
  TOP_RIGHT: 'RIGHT_TOP',
  BOTTOM_LEFT: 'LEFT_BOTTOM',
  BOTTOM_RIGHT: 'RIGHT_BOTTOM'
};

const PIP_POSITION_TO_CORNER: Record<PipPosition, PipCorner> = {
  LEFT_TOP: 'TOP_LEFT',
  RIGHT_TOP: 'TOP_RIGHT',
  LEFT_BOTTOM: 'BOTTOM_LEFT',
  RIGHT_BOTTOM: 'BOTTOM_RIGHT',
  // Middle positions have no corner equivalent in this 4-way picker; fall back
  // to the nearest corner so the selector still shows a sensible selection.
  TOP_MIDDLE: 'TOP_RIGHT',
  BOTTOM_MIDDLE: 'BOTTOM_RIGHT',
  LEFT_MIDDLE: 'BOTTOM_LEFT',
  RIGHT_MIDDLE: 'BOTTOM_RIGHT'
};

export function pipPositionToCorner(pipPosition: PipPosition | null | undefined): PipCorner {
  return pipPosition ? PIP_POSITION_TO_CORNER[pipPosition] : 'BOTTOM_RIGHT';
}

export const SPEAKER_POSITION_OPTIONS: { value: SpeakerPosition; label: string }[] = [
  { value: 'LEFT', label: 'Left' },
  { value: 'RIGHT', label: 'Right' }
];

export const SPEAKER_TILE_SHAPE_OPTIONS: { value: SpeakerTileShape; label: string }[] = [
  { value: 'SQUARE', label: 'Square' },
  { value: 'ROUND', label: 'Round' }
];

export const PIP_CORNER_OPTIONS: PipCorner[] = ['TOP_LEFT', 'TOP_RIGHT', 'BOTTOM_LEFT', 'BOTTOM_RIGHT'];
